/**
 * Governance Data Consistency Service
 *
 * Purpose: Validate consistency between Health and Crisis data for single-community governance
 * Scope: SubPanel-Elect entity - validates data for ONE community only
 *
 * PHASE 2: Data Consistency & Validation
 * - Ensures Health metrics align with active Crisis events
 * - Detects discrepancies in crisis counts, severity levels, resident impact
 * - Provides validation warnings for dashboard display
 */

import type {
  CommunityHealthScore,
  CrisisEvent,
} from '@/types/entities/community_subpanel-elect/governance';
import type { SingleCommunityHealth } from '@/hooks/entities/community_subpanel-elect/useSingleCommunityHealth';

/**
 * Type for health data that can be validated
 * Accepts either base CommunityHealthScore or our custom SingleCommunityHealth
 */
type ValidatableHealthData = CommunityHealthScore | SingleCommunityHealth;

/**
 * Single-community data consistency result
 */
export interface SingleCommunityConsistency {
  tenantId: string;
  tenantName: string;
  isConsistent: boolean;
  lastChecked: string;
  warnings: ConsistencyWarning[];
  criticalCount: {
    fromHealth: number;
    fromCrisis: number;
    match: boolean;
  };
  residentImpact: {
    fromHealth: number;
    fromCrisis: number;
    match: boolean;
  };
  severityAlignment: {
    healthRiskLevel: 'low' | 'medium' | 'high' | 'critical';
    activeCrisisSeverity: 'minor' | 'moderate' | 'major' | 'critical' | 'catastrophic' | 'none';
    aligned: boolean;
  };
}

/**
 * Consistency warning details
 */
export interface ConsistencyWarning {
  level: 'info' | 'warning' | 'error';
  category: 'crisis_count' | 'severity_mismatch' | 'resident_impact' | 'data_staleness';
  message: string;
  healthValue?: number | string;
  crisisValue?: number | string;
  recommendation?: string;
}

/**
 * Governance Data Consistency Service Class
 */
class GovernanceDataConsistencyService {
  /**
   * Validate consistency between Health and Crisis data for a single community
   *
   * @param tenantId - Community tenant identifier
   * @param healthData - Community health scores (optional - will be fetched if not provided)
   * @param crisisData - Active crisis events (optional - will be fetched if not provided)
   * @returns SingleCommunityConsistency with validation results and warnings
   */
  async validateSingleCommunity(
    tenantId: string,
    healthData?: ValidatableHealthData,
    crisisData?: CrisisEvent[],
  ): Promise<SingleCommunityConsistency> {
    const warnings: ConsistencyWarning[] = [];

    // If data not provided, we can't validate (service layer responsibility to fetch)
    if (!healthData || !crisisData) {
      return {
        tenantId,
        tenantName: 'Unknown Community',
        isConsistent: false,
        lastChecked: new Date().toISOString(),
        warnings: [
          {
            level: 'error',
            category: 'data_staleness',
            message: 'Cannot validate consistency - health or crisis data missing',
            recommendation: 'Ensure both health and crisis data are provided',
          },
        ],
        criticalCount: { fromHealth: 0, fromCrisis: 0, match: false },
        residentImpact: { fromHealth: 0, fromCrisis: 0, match: false },
        severityAlignment: {
          healthRiskLevel: 'low',
          activeCrisisSeverity: 'none',
          aligned: false,
        },
      };
    }

    // Extract crisis counts from health data (if available)
    const healthCrisisCount = this.extractCrisisCountFromHealth(healthData);
    const actualCrisisCount = this.countActiveCrises(crisisData, tenantId);

    // Check crisis count consistency
    const crisisCountMatch = healthCrisisCount === actualCrisisCount;
    if (!crisisCountMatch) {
      warnings.push({
        level: 'warning',
        category: 'crisis_count',
        message: `Active crisis count mismatch detected`,
        healthValue: healthCrisisCount,
        crisisValue: actualCrisisCount,
        recommendation:
          healthCrisisCount > actualCrisisCount
            ? 'Health data may be stale - crisis events may have been resolved'
            : 'New crisis events not reflected in health scores - consider health score recalculation',
      });
    }

    // Calculate resident impact from crisis events
    const crisisResidentImpact = this.calculateCrisisResidentImpact(crisisData, tenantId);
    const healthResidentImpact = this.extractResidentImpactFromHealth(healthData);

    // Check resident impact consistency (allow 10% tolerance for calculation differences)
    const impactTolerance = Math.max(10, crisisResidentImpact * 0.1); // 10% or minimum 10 residents
    const residentImpactMatch =
      Math.abs(healthResidentImpact - crisisResidentImpact) <= impactTolerance;

    if (!residentImpactMatch) {
      warnings.push({
        level: 'warning',
        category: 'resident_impact',
        message: `Resident impact calculations differ between health and crisis data`,
        healthValue: healthResidentImpact,
        crisisValue: crisisResidentImpact,
        recommendation: 'Verify crisis event resident counts match health score calculations',
      });
    }

    // Check severity alignment
    const healthRiskLevel = healthData.risks?.level || 'low';
    const highestCrisisSeverity = this.getHighestCrisisSeverity(crisisData, tenantId);
    const severityAligned = this.checkSeverityAlignment(healthRiskLevel, highestCrisisSeverity);

    if (!severityAligned) {
      warnings.push({
        level: 'warning',
        category: 'severity_mismatch',
        message: `Health risk level doesn't align with active crisis severity`,
        healthValue: healthRiskLevel,
        crisisValue: highestCrisisSeverity,
        recommendation:
          healthRiskLevel === 'low' && highestCrisisSeverity === 'critical'
            ? 'Critical crisis detected but health risk is low - update health assessment'
            : 'Health risk level may need adjustment based on current crisis severity',
      });
    }

    // Check data staleness (health data should be recent if crises are active)
    if (actualCrisisCount > 0) {
      const healthAge = Date.now() - new Date(healthData.lastUpdated).getTime();
      const TWELVE_HOURS = 1000 * 60 * 60 * 12;

      if (healthAge > TWELVE_HOURS) {
        warnings.push({
          level: 'info',
          category: 'data_staleness',
          message: `Health data is ${Math.round(healthAge / (1000 * 60 * 60))} hours old with active crises`,
          recommendation: 'Consider refreshing health scores to reflect current crisis situation',
        });
      }
    }

    return {
      tenantId,
      tenantName: healthData.tenantName,
      isConsistent: warnings.filter((w) => w.level === 'error').length === 0,
      lastChecked: new Date().toISOString(),
      warnings,
      criticalCount: {
        fromHealth: healthCrisisCount,
        fromCrisis: actualCrisisCount,
        match: crisisCountMatch,
      },
      residentImpact: {
        fromHealth: healthResidentImpact,
        fromCrisis: crisisResidentImpact,
        match: residentImpactMatch,
      },
      severityAlignment: {
        healthRiskLevel,
        activeCrisisSeverity: highestCrisisSeverity,
        aligned: severityAligned,
      },
    };
  }

  /**
   * Extract crisis count from health data structure
   * Looks for crisis-related metrics in health scores
   */
  private extractCrisisCountFromHealth(health: ValidatableHealthData): number {
    // Check if it's SingleCommunityHealth (has activeCrisisCount)
    if ('activeCrisisCount' in health.risks && typeof health.risks.activeCrisisCount === 'number') {
      return health.risks.activeCrisisCount;
    }

    // Fallback for CommunityHealthScore
    // Check if health data has crisis count (may be in various locations)
    // This is a fallback - ideally health data should include crisis count

    // Check governance.disputeFrequency as proxy (if disputes spike, crises may too)
    const governanceMetrics = health.metrics?.governance as Record<string, unknown> | undefined;
    const disputeFreq =
      governanceMetrics && 'disputeFrequency' in governanceMetrics
        ? (governanceMetrics.disputeFrequency as number)
        : 0;

    // For now, estimate from risk factors mentioning "crisis"
    const crisisRiskFactors =
      health.risks?.factors?.filter((factor) => factor.toLowerCase().includes('crisis')).length ||
      0;

    // Return best estimate (this should be replaced with actual crisis count field in health data)
    return Math.max(disputeFreq, crisisRiskFactors);
  }

  /**
   * Count active crises for a specific community
   */
  private countActiveCrises(crises: CrisisEvent[], tenantId: string): number {
    return crises.filter((crisis) => {
      // Check if crisis affects this tenant
      const affectsThisTenant = crisis.affectedCommunities?.some((ac) => ac.tenantId === tenantId);

      // Only count active or monitoring status
      const isActive = crisis.status === 'active' || crisis.status === 'monitoring';

      return affectsThisTenant && isActive;
    }).length;
  }

  /**
   * Calculate total resident impact from active crises
   */
  private calculateCrisisResidentImpact(crises: CrisisEvent[], tenantId: string): number {
    return crises
      .filter((crisis) => {
        const affectsThisTenant = crisis.affectedCommunities?.some(
          (ac) => ac.tenantId === tenantId,
        );
        const isActive = crisis.status === 'active' || crisis.status === 'monitoring';
        return affectsThisTenant && isActive;
      })
      .reduce((total, crisis) => {
        const affectedCommunity = crisis.affectedCommunities?.find(
          (ac) => ac.tenantId === tenantId,
        );
        return total + (affectedCommunity?.residentCount || 0);
      }, 0);
  }

  /**
   * Extract resident impact from health data
   */
  private extractResidentImpactFromHealth(health: ValidatableHealthData): number {
    // This is a placeholder - health data should include resident impact metric
    // For now, estimate from community cohesion and engagement metrics
    const socialMetrics = health.metrics?.social as Record<string, unknown> | undefined;
    const cohesion =
      socialMetrics && 'communityCohesin' in socialMetrics
        ? (socialMetrics.communityCohesin as number)
        : 100;
    const engagement =
      socialMetrics && 'engagementLevel' in socialMetrics
        ? (socialMetrics.engagementLevel as number)
        : 100;

    // Lower cohesion/engagement suggests resident impact (inverse relationship)
    const impactScore = (200 - cohesion - engagement) / 2;

    // Convert to rough resident count (this should be replaced with actual data)
    return Math.round(impactScore * 2); // Placeholder calculation
  }

  /**
   * Get highest crisis severity for a community
   */
  private getHighestCrisisSeverity(
    crises: CrisisEvent[],
    tenantId: string,
  ): 'minor' | 'moderate' | 'major' | 'critical' | 'catastrophic' | 'none' {
    const severityLevels = ['minor', 'moderate', 'major', 'critical', 'catastrophic'];

    const activeCrises = crises.filter((crisis) => {
      const affectsThisTenant = crisis.affectedCommunities?.some((ac) => ac.tenantId === tenantId);
      const isActive = crisis.status === 'active' || crisis.status === 'monitoring';
      return affectsThisTenant && isActive;
    });

    if (activeCrises.length === 0) return 'none';

    let highestIndex = -1;
    activeCrises.forEach((crisis) => {
      const index = severityLevels.indexOf(crisis.severity);
      if (index > highestIndex) highestIndex = index;
    });

    return severityLevels[highestIndex] as
      'minor' | 'moderate' | 'major' | 'critical' | 'catastrophic';
  }

  /**
   * Check if health risk level aligns with crisis severity
   */
  private checkSeverityAlignment(
    healthRisk: 'low' | 'medium' | 'high' | 'critical',
    crisisSeverity: 'minor' | 'moderate' | 'major' | 'critical' | 'catastrophic' | 'none',
  ): boolean {
    // No crises = low risk is aligned
    if (crisisSeverity === 'none') {
      return healthRisk === 'low' || healthRisk === 'medium';
    }

    // Severity mapping:
    // minor/moderate crisis → low/medium risk OK
    // major crisis → medium/high risk expected
    // critical/catastrophic crisis → high/critical risk expected

    const alignmentMap: Record<string, string[]> = {
      minor: ['low', 'medium'],
      moderate: ['low', 'medium', 'high'],
      major: ['medium', 'high', 'critical'],
      critical: ['high', 'critical'],
      catastrophic: ['critical'],
    };

    return alignmentMap[crisisSeverity]?.includes(healthRisk) || false;
  }

  /**
   * Generate summary message for consistency validation
   */
  getSummaryMessage(consistency: SingleCommunityConsistency): string {
    if (consistency.isConsistent && consistency.warnings.length === 0) {
      return 'Health and Crisis data are fully consistent';
    }

    const errorCount = consistency.warnings.filter((w) => w.level === 'error').length;
    const warningCount = consistency.warnings.filter((w) => w.level === 'warning').length;

    if (errorCount > 0) {
      return `${errorCount} critical consistency error${errorCount > 1 ? 's' : ''} detected`;
    }

    if (warningCount > 0) {
      return `${warningCount} consistency warning${warningCount > 1 ? 's' : ''} detected`;
    }

    return 'Minor consistency issues detected - review recommended';
  }
}

// Export singleton instance
export const governanceDataConsistencyService = new GovernanceDataConsistencyService();
